import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ObjectResponse, ListResponse } from '../interfaces';
import { BaseService } from './base/base.service';
import { AppConfig } from './app-config';
import {
  LoadCompSearchPayload,
  LoadGlobalCompSearchPayload,
  LanguageIsoInfo,
  CompSearchReCycleSearchRead,
  AppliedBulkRejection,
  CompSearchContext,
  CompSearchPayload,
  CompSearchData,
  CompSearchGlobalData,
  CompSearchProjectedCompsInPoolResponse,
  EvaluateComparableTaxpayer
} from '../models';

@Injectable()
export class CompSearchService extends BaseService<any> {

  constructor(protected http: HttpClient, protected config: AppConfig) {
    super(http, config);
  }

  public runCompSearch(context: CompSearchContext): Observable<any> {
    const apiUrl = `${this.getResourceUrl()}/pba/${context.pbaId}/run-compsearch`;

    const body: CompSearchPayload = {
      containerId: context.containerId,
      pbaId: context.pbaId,
      jurisdictionId: context.jurisdictionId,
      executionContext: context.executionContext,
    };

    return this.http.post<any>(apiUrl, body, { observe: 'response' });
  }

  public saveCompSearch(payload: CompSearchData): Observable<void> {
    const apiUrl = `${this.getResourceUrl()}/tenant/${payload.tenantId}/save-comp-search`;
    return this.http.post<void>(apiUrl, payload);
  }

  public saveGlobalCompSearch(payload: CompSearchGlobalData): Observable<void> {
    const apiUrl = `${this.getResourceUrl()}/comp-search-global-library/save`;
    return this.http.post<void>(apiUrl, payload);
  }

  public getSavedCompSearches(tenantId: number): Observable<ObjectResponse<CompSearchData[]>> {
    const apiUrl = `${this.getResourceUrl()}/tenant/${tenantId}/get-comp-searches`;
    return this.http.get<ObjectResponse<CompSearchData[]>>(apiUrl);
  }

  public getSavedGlobalCompSearches(): Observable<ObjectResponse<CompSearchData[]>> {
    const apiUrl = `${this.getResourceUrl()}/comp-search-global-library/get-saved`;
    return this.http.get<ObjectResponse<CompSearchData[]>>(apiUrl);
  }

  public compSearchNameIsUsed(tenantId: number, compSearchName: string): Observable<boolean> {
    return this.getSavedCompSearches(tenantId).pipe(map((compSearches: ObjectResponse<CompSearchData[]>) => {
      return compSearches.data.map(compSearch => compSearch.compSearchName).includes(compSearchName);
    }));
  }

  public compSearchGlobalNameIsUsed(globalCompSearchName: string): Observable<boolean> {
    return this.getSavedGlobalCompSearches().pipe(map((globalCompSearches: ObjectResponse<CompSearchData[]>) => {
      return globalCompSearches.data.map(globalCompSearch => globalCompSearch.compSearchName).includes(globalCompSearchName);
    }));
  }

  public loadCompSearch(payload: LoadCompSearchPayload, compSearchId: number, jurisdictionId: number, containerId: number): Observable<void> {
    const apiUrl = `${this.getResourceUrl()}/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/container/${containerId}/load-comp-search`;
    return this.http.put<void>(apiUrl, payload);
  }

  public loadGlobalCompSearch(payload: LoadGlobalCompSearchPayload, compSearchId: number, jurisdictionId: number, containerId: number): Observable<void> {
    const apiUrl = `${this.getResourceUrl()}/comp-search-global-library/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/container/${containerId}/load`;
    return this.http.put<void>(apiUrl, payload);
  }

  public deleteCompSearch(compSearchLocalLibraryId: number, tenantId: number): Observable<void> {
    const apiUrl = `${this.getResourceUrl()}/saved-comp-search/${compSearchLocalLibraryId}/tenant/${tenantId}/delete-comp-search`;
    return this.http.delete<void>(apiUrl);
  }

  public deleteGlobalCompSearch(compSearchLocalLibraryId: number): Observable<void> {
    const apiUrl = `${this.getResourceUrl()}/comp-search-global-library/${compSearchLocalLibraryId}/delete`;
    return this.http.delete<void>(apiUrl);
  }

  public getBuildComparableSearch(pbaId: number): Observable<any> {
    const apiUrl = `${this.getResourceUrl()}/pba/${pbaId}/jurisdictions`;
    return this.http.get<any>(apiUrl, { observe: 'response' });
  }

  public checkCompSearchStatus(pbaId: number, studyId: number): Observable<any> {
    const apiUrl = `${this.getResourceUrl()}/pba/${pbaId}/compsearch/status`;
    return this.http.request<any>('GET', apiUrl, { body: { studyId }, observe: 'response' });
  }

  public getTuneComparableSearchCriteria(compSearchId: number, jurisdictionId: number):
    Observable<ObjectResponse<any>> {
    const apiUrl = `${this.getResourceUrl()}/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/description`;
    return this.http.get<ObjectResponse<any>>(apiUrl);
  }

  public getTuneCompSearchPlis(compSearchId: number, jurisdictionId: number):
    Observable<ObjectResponse<any>> {
    const apiUrl = `${this.getResourceUrl()}/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/plis`;
    return this.http.get<ObjectResponse<any>>(apiUrl);
  }

  public getRejectionsForPli(compSearchId: number, jurisdictionId: number, pliTypeId: number, pliAvgTypeId: number): Observable<ObjectResponse<any>> {
    const apiUrl = `${this.getResourceUrl()}/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/pli/${pliTypeId}/pli-avg/${pliAvgTypeId}/rejections-for-pli`;
    return this.http.get<ObjectResponse<any>>(apiUrl);
  }

  public getInitialRangeComparables(compSearchId: number, jurisdictionId: number, pliTypeId: number, pliAvgTypeId: number):
    Observable<ListResponse<any>> {
    const apiUrl =
      `${this.getResourceUrl()}/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/pli/${pliTypeId}/pli-avg/${pliAvgTypeId}/intial-range/comparables`;
    return this.http.get<ListResponse<any>>(apiUrl);
  }

  public getInitialRangeResults(compSearchId: number, jurisdictionId: number, pliTypeId: number, pliAvgTypeId: number):
    Observable<ListResponse<any>> {
    const apiUrl =
      `${this.getResourceUrl()}/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/pli/${pliTypeId}/pli-avg/${pliAvgTypeId}/intial-range/range-results`;
    return this.http.get<ListResponse<any>>(apiUrl);
  }

  public getAppliedBulkRejections(context: CompSearchContext): Observable<ListResponse<AppliedBulkRejection>> {
    return this.http.get<ListResponse<AppliedBulkRejection>>(
      `${this.getResourceUrl()}/${context.baseUrl}/bulk-rejections/applied?containerId=${context.containerId}`
    );
  }

  public getNonAppliedBulkRejections(context: CompSearchContext): Observable<ListResponse<AppliedBulkRejection>> {
    return this.http.get<ListResponse<AppliedBulkRejection>>(
      `${this.getResourceUrl()}/${context.baseUrl}/bulk-rejections/non-applied?containerId=${context.containerId}`
    );
  }

  public unapplyBulkRejection(context: CompSearchContext): Observable<ListResponse<AppliedBulkRejection>> {
    return this.http.put<ListResponse<AppliedBulkRejection>>(
      `${this.getResourceUrl()}/${context.baseUrl}/bulk-rejections/${context.rejectionTypeId}/unapply`,
      context
    );
  }

  public applyBulkRejection(context: CompSearchContext): Observable<ListResponse<AppliedBulkRejection>> {
    return this.http.put<ListResponse<AppliedBulkRejection>>(
      `${this.getResourceUrl()}/${context.baseUrl}/bulk-rejections/${context.rejectionTypeId}/apply`,
      context
    );
  }

  public updateCompFineTuningStatus(context: CompSearchContext): Observable<ListResponse<AppliedBulkRejection>> {
    return this.http.put<ListResponse<AppliedBulkRejection>>(
      `${this.getResourceUrl()}/compsearch/${context.compSearchId}/jurisdiction/${context.jurisdictionId}/pli/${context.pliTypeId}/update-fine-tuning-status`,
      context
    );
  }

  public updatePliStatus(context: CompSearchContext): Observable<any> {
    return this.http.put<ListResponse<AppliedBulkRejection>>(
      `${this.getResourceUrl()}/${context.baseUrl}/pli-status`,
      context
    );
  }

  public getEvaluateTaxPayer(context: CompSearchContext): Observable<ListResponse<EvaluateComparableTaxpayer>> {
    return this.http.get<ListResponse<EvaluateComparableTaxpayer>>(
      `${this.getResourceUrl()}/${context.baseUrl}/evaluate-tax-payer?companyIds=${context.sourceIds.join(',')}`
    );
  }

  public getAllRejectionReasons(context: CompSearchContext): Observable<ListResponse<any>> {
    return this.http.get<ListResponse<AppliedBulkRejection>>(
      `${this.getResourceUrl()}/${context.baseUrl}/rejection-reasons`
    );
  }

  public getCompPoolUrls(context: CompSearchContext): Observable<any> {
    return this.http.get<any>(
      `${this.getResourceUrl()}/${context.baseUrl}/comp-pool?${context.getQueryString()}`
    );
  }

  public writeRangeDiscussion(context: CompSearchContext): Observable<any> {
    console.log('context.rangeDiscussion', context.rangeDiscussion);
    return this.http.put<any>(
      `${this.getResourceUrl()}/${context.baseUrl}/range-discussion`,
      { containerId: context.containerId, rangeDiscussion: context.rangeDiscussion }
    );
  }

  public getBulkRejectionReasons(compSearchId: number, jurisdictionId: number, pliTypeId: number, pliAvgTypeId: number): Observable<any> {
    return this.http.get<any>(`${this.getResourceUrl()}/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/pli/${pliTypeId}/pli-avg/${pliAvgTypeId}/reject/reasons`);
  }

  public getReCycleSearchData(
    compSearchId: number,
    jurisdictionId: number
  ): Observable<ObjectResponse<CompSearchReCycleSearchRead>> {
    return this.http.get<ObjectResponse<CompSearchReCycleSearchRead>>(
      `${this.getResourceUrl()}/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/rule-override`
    );
  }

  public projectCompPoolCount(
    compSearchId: number,
    jurisdictionId: number,
    payload
  ): Observable<ObjectResponse<CompSearchProjectedCompsInPoolResponse>> {
    return this.http.put<ObjectResponse<any>>(
      `${this.getResourceUrl()}/compsearch/${compSearchId}/jurisdiction/${jurisdictionId}/comp-pool-count/project`,
      payload
    );
  }

  public getLanguages(jurisdictionId: number, taxYear: number): Observable<ObjectResponse<LanguageIsoInfo[]>> {
    return this.http.get<any>(
      `${this.getResourceUrl()}/compsearch/jurisdiction/${jurisdictionId}/tax-year/${taxYear}/languages`
    );
  }

  public getCompPoolSignedUrls(signedUrl: string): Observable<any> {
    return this.http.get<any>(signedUrl);
  }

  protected getResourceUrl() {
    return `${this.config.getCompSearchApiUrl()}/v1`;
  }
}
